import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CallEndIcon from '@mui/icons-material/CallEnd';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import JitsiCall from './JitsiCall';
import { useCall } from '../../contexts/CallContext';
import useCallLayout from '../../hooks/useCallLayout';

// ─── CallInfoBar ────────────────────────────────────────────────
// Top overlay: participant name, elapsed timer, and live billing info
const CallInfoBar = () => {
  const { activeCall, updateBilling } = useCall();
  const [elapsed, setElapsed] = useState('00:00');

  useEffect(() => {
    if (!activeCall?.callStartTime) return;
    const tick = () => {
      const secs = Math.floor((Date.now() - activeCall.callStartTime) / 1000);
      const m = String(Math.floor(secs / 60)).padStart(2, '0');
      const s = String(secs % 60).padStart(2, '0');
      setElapsed(`${m}:${s}`);
      // Update totalCharged from elapsed time x rate (client-side computation)
      const rate = activeCall.ratePerMin || 0;
      if (rate > 0) {
        updateBilling({ totalCharged: (secs / 60) * rate });
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activeCall?.callStartTime, activeCall?.ratePerMin, updateBilling]);

  const rate = activeCall?.ratePerMin || 0;
  const totalCharged = activeCall?.totalCharged || 0;
  const astrologerEarning = activeCall?.astrologerEarning || 0;
  const hasBilling = rate > 0;

  return (
    <Box sx={{
      position: 'absolute', top: 0, left: 0, right: 0, zIndex: 11,
      display: 'flex', flexDirection: 'column',
      bgcolor: 'rgba(0,0,0,0.5)',
      pointerEvents: 'none',
    }}>
      {/* Row 1: participant name + elapsed timer */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        px: 1.5, py: 0.5,
      }}>
        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500, fontSize: '0.75rem' }}>
          {activeCall?.participantName || 'Connecting...'}
        </Typography>
        <Typography variant="caption" sx={{
          color: '#fff', fontFamily: 'monospace', fontSize: '0.75rem',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {elapsed}
        </Typography>
      </Box>
      {/* Row 2: billing info (rate, charge, earning) — only when rate is known */}
      {hasBilling && (
        <Box
          data-testid="billing-overlay"
          sx={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            gap: 1.5, px: 1, pb: 0.5,
          }}
        >
          <Typography variant="caption" sx={{ color: '#ffd54f', fontSize: '0.65rem' }}>
            {'₹'}{rate}/min
          </Typography>
          <Typography variant="caption" data-testid="user-charge" sx={{ color: '#ef9a9a', fontSize: '0.65rem' }}>
            Spent: {'₹'}{Math.round(totalCharged)}
          </Typography>
          {astrologerEarning > 0 && (
            <Typography variant="caption" data-testid="astrologer-earning" sx={{ color: '#a5d6a7', fontSize: '0.65rem' }}>
              Earned: {'₹'}{Math.round(astrologerEarning)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

// ─── CallToolbar ────────────────────────────────────────────────
// Bottom overlay: Mute, Video, End Call buttons (+ optional User Details, Dashboard)
const CallToolbar = ({ compact = false, onEnd, onUserDetailsClick, onDashboardClick }) => {
  const { activeCall, toggleAudio, toggleVideo } = useCall();
  const iconSize = compact ? 20 : 24;
  const btnSize = compact ? 36 : 44;
  const endBtnSize = compact ? 40 : 52;

  return (
    <Box sx={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      gap: compact ? 1 : 2, py: compact ? 0.5 : 1, px: 1,
      bgcolor: 'rgba(0,0,0,0.6)', borderRadius: compact ? 2 : 0,
    }}>
      {/* Mute */}
      <Tooltip title={activeCall?.isAudioMuted ? 'Unmute' : 'Mute'}>
        <IconButton onClick={toggleAudio} sx={{
          color: activeCall?.isAudioMuted ? '#f44336' : '#fff',
          bgcolor: activeCall?.isAudioMuted ? 'rgba(244,67,54,0.2)' : 'rgba(255,255,255,0.1)',
          width: btnSize, height: btnSize,
          '&:hover': { bgcolor: activeCall?.isAudioMuted ? 'rgba(244,67,54,0.3)' : 'rgba(255,255,255,0.2)' },
        }}>
          {activeCall?.isAudioMuted ? <MicOffIcon sx={{ fontSize: iconSize }} /> : <MicIcon sx={{ fontSize: iconSize }} />}
        </IconButton>
      </Tooltip>

      {/* Video */}
      <Tooltip title={activeCall?.isVideoMuted ? 'Camera on' : 'Camera off'}>
        <IconButton onClick={toggleVideo} sx={{
          color: activeCall?.isVideoMuted ? '#f44336' : '#fff',
          bgcolor: activeCall?.isVideoMuted ? 'rgba(244,67,54,0.2)' : 'rgba(255,255,255,0.1)',
          width: btnSize, height: btnSize,
          '&:hover': { bgcolor: activeCall?.isVideoMuted ? 'rgba(244,67,54,0.3)' : 'rgba(255,255,255,0.2)' },
        }}>
          {activeCall?.isVideoMuted ? <VideocamOffIcon sx={{ fontSize: iconSize }} /> : <VideocamIcon sx={{ fontSize: iconSize }} />}
        </IconButton>
      </Tooltip>

      {/* User Details (expanded modes only) */}
      {onUserDetailsClick && !compact && (
        <Tooltip title="User Details">
          <IconButton onClick={onUserDetailsClick} sx={{
            color: '#fff', bgcolor: 'rgba(255,255,255,0.1)',
            width: btnSize, height: btnSize,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
          }}>
            <PersonIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Dashboard (expanded modes only) */}
      {onDashboardClick && !compact && (
        <Tooltip title="Dashboard">
          <IconButton onClick={onDashboardClick} sx={{
            color: '#fff', bgcolor: 'rgba(255,255,255,0.1)',
            width: btnSize, height: btnSize,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
          }}>
            <DashboardIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>
      )}

      {/* End Call */}
      <Tooltip title="End Call">
        <IconButton onClick={onEnd} sx={{
          color: '#fff', bgcolor: '#f44336',
          width: endBtnSize, height: endBtnSize, borderRadius: '50%',
          '&:hover': { bgcolor: '#d32f2f' },
          boxShadow: '0 2px 12px rgba(244,67,54,0.4)',
        }}>
          <CallEndIcon sx={{ fontSize: iconSize + 2 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// ─── FloatingJitsiPiP ──────────────────────────────────────────
// Three layout modes: pip (floating), fullscreen, split (video + chat)
const FloatingJitsiPiP = ({
  consultationId, chatRoomId, displayName, videoMuted, callType,
  onEnd, onJoined, onUserDetailsClick, onDashboardClick, children,
}) => {
  const { setExpanded } = useCall();
  const { mode, pipSize, pipConfig, isMobile } = useCallLayout();

  // Drag state (PiP mode only)
  const [position, setPosition] = useState(() => ({
    x: typeof window !== 'undefined' ? window.innerWidth - pipConfig.widthDesktop - pipConfig.snapMargin : 0,
    y: typeof window !== 'undefined' ? window.innerHeight - pipConfig.heightDesktop - pipConfig.snapMargin : 0,
  }));
  const [showToolbar, setShowToolbar] = useState(isMobile);
  const isDragging = useRef(false);
  const dragStart = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const cornerRef = useRef({ isRight: true, isBottom: true });
  const swipeStartY = useRef(null);

  // Snap to nearest corner on drag release
  const snapToCorner = useCallback(() => {
    const margin = pipConfig.snapMargin;
    const cx = position.x + pipSize.width / 2;
    const cy = position.y + pipSize.height / 2;
    const midX = window.innerWidth / 2;
    const midY = window.innerHeight / 2;
    const newX = cx < midX ? margin : window.innerWidth - pipSize.width - margin;
    const newY = cy < midY ? margin : window.innerHeight - pipSize.height - margin;
    setPosition({ x: newX, y: newY });
    cornerRef.current = { isRight: cx >= midX, isBottom: cy >= midY };
  }, [position, pipSize, pipConfig.snapMargin]);

  // Recalculate position on window resize (maintain corner)
  useEffect(() => {
    const handleResize = () => {
      const margin = pipConfig.snapMargin;
      const x = cornerRef.current.isRight
        ? window.innerWidth - pipSize.width - margin : margin;
      const y = cornerRef.current.isBottom
        ? window.innerHeight - pipSize.height - margin : margin;
      setPosition({ x, y });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [pipSize, pipConfig.snapMargin]);

  // Keyboard: Esc to minimize
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Escape' && mode !== 'pip') setExpanded(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, setExpanded]);

  // Pointer events: unified mouse + touch drag
  const handlePointerDown = useCallback((e) => {
    if (mode !== 'pip') return;
    if (e.target.closest('button')) return;
    isDragging.current = false;
    dragStart.current = { x: e.clientX, y: e.clientY };
    dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  }, [mode, position]);

  const handlePointerMove = useCallback((e) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    if (!isDragging.current && Math.abs(dx) + Math.abs(dy) < pipConfig.dragThreshold) return;
    isDragging.current = true;
    const maxX = window.innerWidth - pipSize.width;
    const maxY = window.innerHeight - pipSize.height;
    setPosition({
      x: Math.max(0, Math.min(maxX, e.clientX - dragOffset.current.x)),
      y: Math.max(0, Math.min(maxY, e.clientY - dragOffset.current.y)),
    });
  }, [pipSize, pipConfig.dragThreshold]);

  const handlePointerUp = useCallback((e) => {
    if (isDragging.current) snapToCorner();
    dragStart.current = null;
    isDragging.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
  }, [snapToCorner]);

  // Swipe-to-minimize on mobile fullscreen
  const handleTouchStart = useCallback((e) => {
    if (mode !== 'fullscreen') return;
    swipeStartY.current = e.touches[0].clientY;
  }, [mode]);

  const handleTouchEnd = useCallback((e) => {
    if (swipeStartY.current === null) return;
    const dy = e.changedTouches[0].clientY - swipeStartY.current;
    if (dy > 100) setExpanded(false);
    swipeStartY.current = null;
  }, [setExpanded]);

  const toggleExpand = useCallback(() => {
    setExpanded(mode === 'pip');
  }, [mode, setExpanded]);

  // Shared JitsiCall props
  const jitsiProps = { consultationId, chatRoomId, displayName, videoMuted, callType, onEnd, onJoined };

  // ─── FULLSCREEN MODE ────────────────────────────────────────
  if (mode === 'fullscreen') {
    return (
      <Box
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{
          position: 'fixed', inset: 0, zIndex: pipConfig.zIndex + 100,
          bgcolor: '#000', display: 'flex', flexDirection: 'column',
        }}
      >
        <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
          <JitsiCall {...jitsiProps} />
          <CallInfoBar />
          <Tooltip title="Minimize">
            <IconButton onClick={() => setExpanded(false)} sx={{
              position: 'absolute', top: 8, right: 8, zIndex: 12,
              color: '#fff', bgcolor: 'rgba(0,0,0,0.4)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
            }}>
              <FullscreenExitIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <CallToolbar onEnd={onEnd} onUserDetailsClick={onUserDetailsClick} onDashboardClick={onDashboardClick} />
      </Box>
    );
  }

  // ─── SPLIT MODE ──────────────────────────────────────────────
  if (mode === 'split') {
    const videoWidth = `${pipConfig.splitRatio * 100}%`;
    const chatWidth = `${(1 - pipConfig.splitRatio) * 100}%`;
    return (
      <Box sx={{
        position: 'fixed', inset: 0, zIndex: pipConfig.zIndex + 100,
        display: 'flex', flexDirection: 'row', bgcolor: '#000',
      }}>
        {/* Video panel */}
        <Box sx={{ width: videoWidth, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
            <JitsiCall {...jitsiProps} />
            <CallInfoBar />
            <Tooltip title="Minimize">
              <IconButton onClick={() => setExpanded(false)} sx={{
                position: 'absolute', top: 8, right: 8, zIndex: 12,
                color: '#fff', bgcolor: 'rgba(0,0,0,0.4)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
              }}>
                <FullscreenExitIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <CallToolbar onEnd={onEnd} onUserDetailsClick={onUserDetailsClick} onDashboardClick={onDashboardClick} />
        </Box>
        {/* Chat panel (children) */}
        <Box sx={{ width: chatWidth, bgcolor: '#fff', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      </Box>
    );
  }

  // ─── PIP MODE ────────────────────────────────────────────────
  return (
    <Box
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerEnter={() => !isMobile && setShowToolbar(true)}
      onPointerLeave={() => !isMobile && setShowToolbar(false)}
      sx={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: pipSize.width,
        height: pipSize.height,
        zIndex: pipConfig.zIndex,
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        border: '2px solid rgba(255,255,255,0.15)',
        bgcolor: '#000',
        cursor: 'grab',
        touchAction: 'none',
        '&:active': { cursor: 'grabbing' },
        transition: isDragging.current ? 'none' : 'left 0.3s ease, top 0.3s ease',
      }}
    >
      {/* Video (pointer-events none so drag works through it) */}
      <Box sx={{ width: '100%', height: '100%', pointerEvents: 'none' }}>
        <JitsiCall {...jitsiProps} />
      </Box>

      {/* Info bar (always visible) */}
      <CallInfoBar />

      {/* Expand button */}
      <Tooltip title="Expand">
        <IconButton onClick={toggleExpand} size="small" sx={{
          position: 'absolute', top: 4, right: 4, zIndex: 12,
          color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', p: 0.5,
          '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
        }}>
          <FullscreenIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      {/* Toolbar (hover on desktop, always on mobile) */}
      <Box
        onClick={() => isMobile && setShowToolbar((s) => !s)}
        sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 12,
          opacity: showToolbar ? 1 : 0,
          transform: showToolbar ? 'translateY(0)' : 'translateY(100%)',
          transition: 'opacity 0.2s, transform 0.2s',
          pointerEvents: 'auto',
        }}
      >
        <CallToolbar compact onEnd={onEnd} />
      </Box>
    </Box>
  );
};

export default FloatingJitsiPiP;
